import {
  Box,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded'
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded'
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { NavLink, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { useAdminAuth } from '../hooks/useAdminAuth'

const NAV = [
  { to: 'import', label: 'CSV Import', icon: CloudUploadRoundedIcon, desc: 'Upload & process listings' },
  { to: 'bnb-enrichment', label: 'BNB Enrichment', icon: TravelExploreRoundedIcon, desc: 'AI contact discovery' },
  { to: 'airbnb-scraper', label: 'City Scraper', icon: HomeWorkRoundedIcon, desc: 'City listings & enrich' },
]

function currentPage(pathname: string) {
  return NAV.find((item) => pathname.includes(item.to)) || NAV[0]
}

function NavItems({
  onNavigate,
  compact,
}: {
  onNavigate?: () => void
  compact?: boolean
}) {
  const location = useLocation()
  return (
    <List disablePadding sx={{ px: compact ? 0.75 : 1 }}>
      {NAV.map(({ to, label, icon: Icon, desc }) => {
        const active =
          location.pathname === `/admin-new/${to}` ||
          (to === 'import' && (location.pathname === '/admin-new' || location.pathname === '/admin-new/'))
        return (
          <ListItemButton
            key={to}
            component={NavLink}
            to={to}
            onClick={onNavigate}
            selected={active}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              px: compact ? 1 : 1.5,
              justifyContent: compact ? 'center' : 'flex-start',
              color: '#ffffff',
              '&.Mui-selected': {
                bgcolor: 'rgba(201,162,39,0.14)',
                borderLeft: compact ? 'none' : '3px solid #C9A227',
                color: '#ffffff',
                '&:hover': { bgcolor: 'rgba(201,162,39,0.18)' },
              },
              '& .MuiListItemText-primary, & .MuiListItemText-secondary, & .MuiTypography-root': {
                color: '#ffffff !important',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: compact ? 0 : 40, color: active ? '#C9A227' : 'rgba(250,248,245,0.55)' }}>
              <Icon fontSize="small" />
            </ListItemIcon>
            {!compact && (
              <ListItemText
                primary={label}
                secondary={desc}
                primaryTypographyProps={{ fontWeight: 600, fontSize: 14, color: '#ffffff' }}
                secondaryTypographyProps={{ fontSize: 11, color: '#ffffff', noWrap: true }}
              />
            )}
          </ListItemButton>
        )
      })}
    </List>
  )
}

export default function AdminLayout({
  apiOnline,
  isRunning,
  children,
}: {
  apiOnline: boolean
  isRunning?: boolean
  children: React.ReactNode
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { admin, logout } = useAdminAuth()
  const location = useLocation()
  const page = currentPage(location.pathname)
  const PageIcon = page.icon
  const compact = isTablet

  const sidebar = (mode: 'full' | 'compact' | 'drawer') => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', py: 2 }}>
      <Box sx={{ px: mode === 'compact' ? 1 : 2.5, mb: 2 }}>
        {mode === 'compact' ? (
          <Typography variant="h6" sx={{ color: '#C9A227', fontWeight: 800, textAlign: 'center' }}>
            A
          </Typography>
        ) : (
          <>
            <Typography variant="h6" sx={{ color: '#faf8f5', fontWeight: 700, letterSpacing: '-0.02em' }}>
              AonlaStay <span style={{ color: '#C9A227' }}>Ops</span>
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(250,248,245,0.45)' }}>
              Listing ops console
            </Typography>
          </>
        )}
      </Box>
      <NavItems compact={mode === 'compact'} onNavigate={() => mode === 'drawer' && setDrawerOpen(false)} />
      <Box sx={{ flex: 1 }} />
      {mode !== 'compact' && (
        <Box sx={{ px: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Chip
            label={admin?.username || 'admin'}
            size="small"
            sx={{ bgcolor: 'rgba(212,166,87,0.12)', color: '#E8D5A3', mb: 1, width: '100%' }}
          />
          <ListItemButton onClick={logout} sx={{ borderRadius: 2, color: '#ffffff' }}>
            <ListItemIcon sx={{ minWidth: 36, color: '#ffffff' }}>
              <LogoutRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Sign out" primaryTypographyProps={{ fontSize: 14, color: '#ffffff' }} />
          </ListItemButton>
        </Box>
      )}
    </Box>
  )

  return (
    <Box className="admin-app admin-dashboard-bg" sx={{ display: 'flex', minHeight: '100dvh', width: '100%' }}>
      {!isMobile && (
        <Box
          className="admin-sidebar"
          sx={{
            width: compact ? 76 : 248,
            flexShrink: 0,
            borderRight: '1px solid rgba(255,255,255,0.06)',
            bgcolor: '#0c0f14',
            position: 'sticky',
            top: 0,
            height: '100dvh',
            overflowY: 'auto',
          }}
        >
          {sidebar(compact ? 'compact' : 'full')}
        </Box>
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 'min(320px, 88vw)', bgcolor: '#0c0f14' }, className: 'admin-sidebar' }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#faf8f5' }} aria-label="close menu">
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        {sidebar('drawer')}
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          className="admin-glass-header"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 120,
            px: { xs: 1.25, sm: 2, lg: 3 },
            py: { xs: 1, sm: 1.25 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            minHeight: { xs: 56, sm: 64 },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
            {isMobile && (
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: '#faf8f5' }} aria-label="menu">
                <MenuRoundedIcon />
              </IconButton>
            )}
            <PageIcon sx={{ color: '#C9A227', fontSize: { xs: 20, sm: 22 }, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ color: '#faf8f5', fontWeight: 700, lineHeight: 1.2, fontSize: { xs: 15, sm: 17 } }}
                noWrap
              >
                {page.label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(250,248,245,0.5)', display: { xs: 'none', sm: 'block' } }} noWrap>
                {page.desc}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1 }} sx={{ flexShrink: 0 }}>
            <Chip
              icon={
                <FiberManualRecordIcon
                  sx={{
                    fontSize: '10px !important',
                    color: `${apiOnline ? '#2d9d78' : '#d64545'} !important`,
                    ...(isRunning && { animation: 'admin-pulse 2s infinite' }),
                  }}
                />
              }
              label={isMobile ? (apiOnline ? 'API' : 'Off') : `API ${apiOnline ? 'online' : 'offline'}${isRunning ? ' · job' : ''}`}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.08)',
                color: 'rgba(250,248,245,0.8)',
                height: 28,
                '& .MuiChip-label': { px: { xs: 0.75, sm: 1 } },
              }}
            />
            {isMobile && (
              <IconButton onClick={logout} sx={{ color: 'rgba(250,248,245,0.7)' }} aria-label="sign out">
                <LogoutRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Box>
    </Box>
  )
}
